import React, {
  useState, useEffect, forwardRef, useImperativeHandle,
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from 'react-apollo';
import { useToasts } from 'react-toast-notifications';
import LoadingOverlay from 'react-loading-overlay';
import Paginator from 'react-hooks-paginator';
import { map } from 'lodash';
import {
  Request, Deactivate, DeleteUser, ArtistDetail,
} from '../../modals';
import {
  LOGO_LARGE_BLACK, staticConstants, handleGraphqlError, dateFormats, loaderStyle, NUMERIC_VALUE,
} from '../../../utils';
import { GET_ALL_USER_QUERY } from '../../../graphql-client/queries';
import { UPDATE_USER, LOADER_MUTATION } from '../../../graphql-client/mutations';
import ArtistItemList from './ArtistItemList';
import EditProfileDetail from './EditProfileDetail';
import RenderTableHeader from './RenderTableHeader';

const ArtistList = forwardRef((props, ref) => {
  const { addToast } = useToasts();
  const [requestModal, setRequestModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editArtistModal, setEditArtistModal] = useState(false);
  const [modalObj, setModalObj] = useState({});
  const pageLimit = NUMERIC_VALUE.TWENTY;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKey, setSearchKey] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [artistDetailModal, setArtistDetailModal] = useState(false);
  const [modalItemObj, setModalItemObj] = useState({});
  const [artistListing, setArtistListing] = useState({});
  const [filter, setFilter] = useState({
    sort: '',
    sortBy: 0,
  });
  const [refreshList, setRefreshList] = useState(false);
  const [userId, setUserId] = useState({});
  const [requestModalObj, setRequestModalObj] = useState({});
  const [filterBrand, setFilterBrand] = useState([]);
  const [filterDemo, setFilterDemo] = useState({});

  const toggleModalPopup = (modalType, artistDetail) => {
    switch (modalType) {
      case staticConstants.MODAL_POPUP_TYPE.REQUESTED:
        setRequestModalObj(artistDetail);
        setRequestModal(!requestModal);
        break;
      case staticConstants.MODAL_POPUP_TYPE.EDIT_PROFILE:
        setUserId(artistDetail._id);
        setEditArtistModal(!editArtistModal);
        break;
      case staticConstants.MODAL_POPUP_TYPE.DEACTIVATED:
        setModalObj(artistDetail);
        setDeactivateModal(!deactivateModal);
        break;
      case staticConstants.MODAL_POPUP_TYPE.DELETE:
        setModalObj(artistDetail);
        setDeleteModal(!deleteModal);
        break;
      default:
        break;
    }
  };
  const toggleArtistDetailModal = (item, img) => {
    const updatedItem = { ...item, img };
    setModalItemObj(updatedItem);
    setArtistDetailModal(!artistDetailModal);
  };

  filterBrand && filterBrand.length && filterBrand.forEach((el) => {
    delete el.__typename;
    el.subCategoryId.map((subEl) => delete subEl.__typename);
  });

  const queryObj = {
    role: staticConstants.ROLE.ARTIST,
    page: currentPage,
    limit: pageLimit,
    searchKey,
    statusFilter,
    brandFilter: filterBrand,
    demoFilter: filterDemo,
    ...filter,
  };

  const classNameObj = {
    flexCol10: 'd-flex-table-col-10',
  };

  useImperativeHandle(ref, () => ({
    searchArtist(search, filterValue, filterBrands, isDemoFilter) {
      setCurrentPage(1);
      setSearchKey(search);
      setStatusFilter(filterValue);
      setFilter({
        sort: '',
        sortBy: 0,
      });
      if (isDemoFilter) {
        setFilterDemo(filterBrands);
      } else {
        setFilterBrand(filterBrands);
      }
    },
  }));

  const refreshUserList = () => {
    setRefreshList(true);
  };

  const { loading, data, refetch: getArtistData } = useQuery(
    GET_ALL_USER_QUERY,
    { variables: queryObj, fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (data && data.getAllUser) {
      props.setTotalCount(data.getAllUser.count);
      setArtistListing(data.getAllUser.data);
    }
  }, [data]);

  useEffect(() => {
    if (currentPage || filter || searchKey || refreshList) {
      getArtistData();
      setRefreshList(false);
    }
  },
  [currentPage, filter, searchKey, refreshList]);

  const tableContent = {
    name: { name: 'Artist', class: 'd-flex-table-col-13', sort: true },
    email: { name: 'Email Address', class: 'd-flex-table-col-16', sort: false },
    contactNumber: { name: 'Phone', class: `${classNameObj.flexCol10}`, sort: false },
    city: { name: 'City', class: `${classNameObj.flexCol10}`, sort: true },
    label: { name: 'Label', class: `${classNameObj.flexCol10}`, sort: true },
    createdAt: { name: 'Registered on', class: 'd-flex-table-col-11', sort: true },
    status: { name: 'Status', class: 'd-flex-table-col-11', sort: true },
    total: { name: 'Total $', class: 'd-flex-table-col-9', sort: false },
    totalOffers: { name: 'Gigs', class: `${classNameObj.flexCol10}`, sort: true },
  };

  const sortData = (sort, sortBy) => {
    setFilter({ sort, sortBy });
  };

  const [loaderMutate] = useMutation(LOADER_MUTATION, {
    variables: { show: false },
  });

  const [updateArtistProfile] = useMutation(UPDATE_USER, {
    variables: {},
    onError(err) {
      loaderMutate({ variables: { show: false } });
      addToast(handleGraphqlError(err), { appearance: 'error' });
    },

    onCompleted() {
      loaderMutate({ variables: { show: false } });
      refreshUserList();
    },
  });

  const onSubmitNote = (reqObj) => {
    loaderMutate({ variables: { show: true } });
    updateArtistProfile({ variables: reqObj });
  };

  const updateItem = (item, img) => {
    const updatedItem = { ...item, img };
    setModalItemObj(updatedItem);
  };

  const renderArtistList = () => {
    let dataList = []
    if (data && data.getAllUser) {
      return map(artistListing, (item, index) => {
        const fetchedProfileImage = item && item.artistImage !== ''
          ? item.artistImage : LOGO_LARGE_BLACK;
        const registerDate = moment(item.createdAt).format(dateFormats.DATE_WITH_YEAR);
        return item && (
          <ArtistItemList
            item={item}
            key={index}
            fetchedProfileImage={fetchedProfileImage}
            registerDate={registerDate}
            currentIndex={index}
            toggleArtistDetailModal={toggleArtistDetailModal}
            toggleRequestModal={() => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.REQUESTED, item); }}
            submitNote={onSubmitNote}
            toggleDeactivateModal={() => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.DEACTIVATED, item); }}
            toggleEditArtistModal={() => toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.EDIT_PROFILE, item)}
            toggleDeleteModal={() => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.DELETE, item); }}
            updateItem={updateItem}
          />
        );
      });
    }
    dataList = []
    return null;
  };
  return (
    <div className="table-wrapper">
      <div className={`d-flex-table ${data && data.getAllUser && data.getAllUser.count === 0 ? 'no-result' : ''}`}>
        <div className="d-flex-table-head">
          <div className="d-flex-table-row">
            <RenderTableHeader
              tableContent={tableContent}
              filter={filter}
              sortData={sortData}
            />
          </div>
        </div>
        <div className="d-flex-table-body">
          <LoadingOverlay
            active={loading}
            spinner
            text=""
            className="offerListContainerLoader"
            styles={loaderStyle}
          />
          <div className="d-flex-table-body-wrapper">
            {renderArtistList()}
            {data && data.getAllUser && data.getAllUser.paginationCount === 0 && (
              <div className="no-offer-table-data">
                <div className="no-table-data">
                  <h2>No Results found</h2>
                  <p>Try using a different name or keyword</p>
                </div>
              </div>
            )}
          </div>
          <Paginator
            totalRecords={data && data.getAllUser && data.getAllUser.paginationCount ? data.getAllUser.paginationCount : 0}
            pageLimit={pageLimit}
            pageNeighbours={NUMERIC_VALUE.TWO}
            setOffset={() => { }}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageNextClass="paginationNextButton"
            pagePrevClass="paginationPrevButton"
          />
        </div>
      </div>
      {(requestModal && requestModalObj) && (
        <Request
          isOpen={requestModal}
          onClose={() => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.REQUESTED); }}
          userDetail={requestModalObj}
          userRole={staticConstants.ROLE.ARTIST}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {(deactivateModal && modalObj) && (
        <Deactivate
          isOpen={deactivateModal}
          onClose={() => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.DEACTIVATED); }}
          userDetail={modalObj}
          role={staticConstants.ROLE_TYPE.ARTIST}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {(deleteModal && modalObj) && (
      <DeleteUser
        isOpen={deleteModal}
        onClose={() => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.DELETE); }}
        userDetail={modalObj}
        role={staticConstants.ROLE_TYPE.ARTIST}
        updateUserList={() => { refreshUserList(); }}
      />
      )}
      {(editArtistModal && userId) && (
        <EditProfileDetail
          isOpen={editArtistModal}
          userId={userId}
          userRole={staticConstants.ROLE.ARTIST}
          onClose={(isOpen) => { toggleModalPopup(staticConstants.MODAL_POPUP_TYPE.EDIT_PROFILE, isOpen); }}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
      {artistDetailModal && (
        <ArtistDetail
          isOpen={artistDetailModal}
          onClose={() => { toggleArtistDetailModal(); }}
          userId={modalItemObj._id}
          isCurrentUserDeactive={modalItemObj.status === staticConstants.STATUS_TYPE.DEACTIVATED}
          updateUserList={() => { refreshUserList(); }}
        />
      )}
    </div>
  );
});

ArtistList.propTypes = {
  setTotalCount: PropTypes.number.isRequired,
};

export default ArtistList;
